import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Lock, Sparkles, X, Loader2, Languages } from "lucide-react";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useTranslation } from "react-i18next";

/**
 * Global lesson search across every course in the current language.
 *
 * Locked lessons are surfaced — that's the point, discovery — but clicking
 * one shows a hint about what to complete first instead of navigating.
 * Only unlocked lessons open the player.
 */
export default function CourseSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Debounced fetch — cheap client-side timer keeps the field responsive.
  useEffect(() => {
    if (q.trim().length < 2) {
      setItems([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      api.get(`/search/lessons?q=${encodeURIComponent(q.trim())}&language=${lang}`)
        .then((r) => setItems(r.data.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(handle);
  }, [q, lang]);

  // Close on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openLesson = (r) => {
    if (r.tier_locked) {
      toast.info(t("courseSearchUi.tierLockedToast", { tier: r.tier }));
      return;
    }
    if (r.sequence_locked) {
      if (r.lock_reason === "prev_chapter" && r.prev_chapter_number) {
        toast.info(t("courseSearchUi.prevChapterQuizToast", { chapterNumber: r.prev_chapter_number }));
      } else {
        toast.info(t("courseSearchUi.prevLessonToast"));
      }
      // Take them to the course page so they can see what to do next.
      navigate(`/courses/${r.course_id}`);
      return;
    }
    navigate(`/lessons/${r.lesson_id}`);
  };

  return (
    <div ref={boxRef} className="relative max-w-2xl">
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t("courseSearchUi.searchPlaceholder")}
          data-testid="course-search-input"
          className="w-full h-11 pl-10 pr-9 rounded-full bg-white border border-slate-200 focus:border-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-800/10 text-sm text-slate-900 placeholder:text-slate-400 transition-colors"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setItems([]); }}
            data-testid="course-search-clear"
            aria-label={t("courseSearchUi.clearSearchAriaLabel")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900 transition-colors"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          data-testid="course-search-results"
          className="absolute z-30 mt-2 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-[520px] overflow-y-auto"
        >
          {loading && (
            <div className="p-6 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin" /> {t("courseSearchUi.searching")}
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-6 text-sm text-slate-500">
              {t("courseSearchUi.noResults")} <span className="font-semibold text-slate-800">"{q}"</span>. {t("courseSearchUi.tryDifferent")}
            </div>
          )}
          {!loading && items.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {items.map((r) => {
                const anyLock = r.tier_locked || r.sequence_locked;
                return (
                  <li key={r.lesson_id}>
                    <button
                      type="button"
                      onClick={() => openLesson(r)}
                      data-testid={`course-search-result-${r.lesson_id}`}
                      className="w-full text-left px-5 py-3.5 hover:bg-slate-50 flex items-start gap-3 transition-colors"
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        anyLock ? "bg-slate-100 text-slate-400" : "bg-blue-800/10 text-blue-800"
                      }`}>
                        {anyLock ? <Lock size={14} /> : <Sparkles size={14} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                          <span className="truncate">{r.course_title}</span>
                          <span className="text-slate-300">·</span>
                          <span className="truncate">{t("courseSearchUi.chapterLabel", { number: String(r.chapter_number).padStart(2, "0") })} · {r.chapter_title}</span>
                        </div>
                        <div className="mt-0.5 text-sm font-semibold text-slate-900 leading-snug truncate">
                          {r.lesson_title}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                          {r.duration && <span>{r.duration}</span>}
                          <span className="inline-flex items-center gap-1 uppercase tracking-widest font-semibold text-slate-500">
                            {r.tier}
                          </span>
                          {r.content_language && r.content_language !== lang && (
                            <span className="inline-flex items-center gap-1 text-blue-800 font-semibold">
                              <Languages size={11} /> {r.content_language.toUpperCase()}
                            </span>
                          )}
                          {r.tier_locked && (
                            <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                              {t("courseSearchUi.upgradeTo", { tier: r.tier })}
                            </span>
                          )}
                          {!r.tier_locked && r.sequence_locked && (
                            <span className="inline-flex items-center gap-1 text-slate-600 font-semibold">
                              {r.lock_reason === "prev_chapter" && r.prev_chapter_number
                                ? t("courseSearchUi.passChapterQuiz", { chapterNumber: r.prev_chapter_number })
                                : t("courseSearchUi.completePrevLesson")}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
